'use client';

import * as React from 'react';
import { styled } from '@linaria/react';
import { Volume } from 'lucide-react';

import useKeybindings from '@/hooks/use-keybindings';
import useSound from '@/hooks/use-sound';

import { UserPreferencesContext } from '@/components/UserPreferencesProvider';
import TextLink from '@/components/Link/TextLink';
import VisuallyHidden from '@/components/VisuallyHidden';

import BaseChild from './BaseChild';
import DrumButton from './DrumButton';

const SOUND_URL = '/sounds/909-drums.mp3';

function CardPopularPackages(
  delegated: React.HTMLAttributes<HTMLDivElement>
) {
  const [keyHeld, setKeyHeld] = React.useState<string | null>(null);
  const [showMuteWarning, setShowMuteWarning] = React.useState(false);
  const { soundEnabled } = React.useContext(UserPreferencesContext);

  const [playRaw] = useSound(SOUND_URL, {
    sprite: {
      kick: [0, 350],
      hihat: [374, 160],
      snare: [666, 290],
      cowbell: [968, 200],
    },
  });

  const play = React.useCallback(
    (...args: any) => {
      if (soundEnabled) {
        playRaw(...args);
      } else {
        setShowMuteWarning(true);
      }
    },
    [playRaw, soundEnabled]
  );

  React.useEffect(() => {
    if (soundEnabled) {
      setShowMuteWarning(false);
    }
  }, [soundEnabled]);

  useKeybindings(
    {
      Q: () => {
        play({ id: 'kick' });
        setKeyHeld('Q');
      },
      W: () => {
        play({ id: 'hihat' });
        setKeyHeld('W');
      },
      E: () => {
        play({ id: 'snare' });
        setKeyHeld('E');
      },
      R: () => {
        play({ id: 'cowbell' });
        setKeyHeld('R');
      },
    },
    {
      ignoreWhileMetaKey: true,
    }
  );

  const releaseKey = React.useCallback(() => {
    setKeyHeld(null);
  }, []);

  useKeybindings(
    {
      Q: releaseKey,
      W: releaseKey,
      E: releaseKey,
      R: releaseKey,
    },
    {
      event: 'keyup',
    }
  );

  return (
    <Wrapper {...delegated}>
      <Actions>
        <DrumButton
          id="kick"
          keybinding="Q"
          isPressed={keyHeld === 'Q'}
          play={play}
        />
        <DrumButton
          id="hihat"
          keybinding="W"
          isPressed={keyHeld === 'W'}
          play={play}
        />
        <DrumButton
          id="snare"
          keybinding="E"
          isPressed={keyHeld === 'E'}
          play={play}
        />
        <DrumButton
          id="cowbell"
          keybinding="R"
          isPressed={keyHeld === 'R'}
          play={play}
        />
      </Actions>
      <p>
        I'm the author of several popular open-source packages,
        including{' '}
        <TextLink href="https://www.npmjs.com/package/use-sound">
          use-sound
        </TextLink>
        , a tool for playing sound effects, and{' '}
        <TextLink href="https://www.npmjs.com/package/react-flip-move">
          react-flip-move
        </TextLink>
        , an animation library.
      </p>
      <p>
        My packages are downloaded <em>~600,000 times per month</em>{' '}
        according to NPM.
      </p>
      {showMuteWarning && (
        <Warning>
          These keys typically play a drum sound, but you currently
          have sound muted. Click the{' '}
          <Volume aria-hidden="true" size={16} />
          <VisuallyHidden>Volume</VisuallyHidden> icon in the header
          to enable sound.
        </Warning>
      )}
    </Wrapper>
  );
}

const Wrapper = styled(BaseChild)``;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const Warning = styled.p`
  padding: 16px;
  background: var(--color-cloud-500);
  animation: fadeIn 400ms;
  margin: -8px;
  margin-top: 24px;
  border-radius: 8px;
  font-size: 0.875rem;

  svg {
    display: inline-block;
    /* Optical alignment: */
    transform: translate(0.125em, 0.125em);
  }
`;

export default React.memo(CardPopularPackages);
